<template>
  <main
    class="main-content flex-grow-1 p-4 overflow-auto mt-6 d-flex flex-column gap-3"
    style="background-color: #f8f9fa"
  >
    <div class="d-flex justify-content-between align-items-center mt-4">
      <h2 class="text-header-page">User Management</h2>
      <button class="custom-button" @click="showInviteModal = true">
        + Invite User
      </button>
    </div>

    <div
      class="d-flex flex-column align-items-start gap-3 flex-fill align-self-stretch border bg-white"
      style="
        border-radius: 16px;
        border-color: var(--Background-Grey, #dae0e2);
        background: var(--Dark-White, #fff);
      "
    >
      <div
        class="d-flex flex-column align-items-start gap-1 px-4 pt-3 pb-0 align-self-stretch"
      >
        <div class="col-auto">
          <div class="input-wrapper search-input-wrapper">
            <span class="input-icon icon-left">
              <i class="fa fa-search"></i>
            </span>
            <input
              type="text"
              placeholder="Search Ref ID"
              v-model="search"
              class="search-input styled-input"
            />
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="card shadow-sm w-100">
        <div
          v-if="loading"
          class="d-flex justify-content-center align-items-center p-5"
        >
          <div class="spinner-border text-success" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <div v-else class="table-responsive">
          <table class="table align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th>Fullname</th>
                <th>Email</th>
                <th>Phone Number</th>
                <th>Role</th>
                <th>Join Date</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(user, index) in filteredUsers" :key="index">
                <td>{{ user.fullname }}</td>
                <td>{{ user.email }}</td>
                <td>{{ user.phone_number }}</td>
                <td>{{ user.role.roles_name }}</td>
                <td>{{ user.join_date ? formatDate(user.join_date) : "-" }}</td>
                <td>
                  <span
                    :class="{
                      'active-badge': user.status === 'active',
                      'pending-badge': user.status === 'pending',
                      'suspended-badge': user.status === 'suspended',
                    }"
                  >
                    <div
                      :class="{
                        'text-active-badge': user.status === 'active',
                        'text-pending-badge': user.status === 'pending',
                        'text-suspended-badge': user.status === 'suspended',
                      }"
                    >
                      {{ capitalizeWords(user.status) }}
                    </div>
                  </span>
                </td>
                <td class="position-relative">
                  <div class="dropdown">
                    <img
                      src="/assets/icons/ic_menu.svg"
                      alt=""
                      width="24px"
                      height="24px"
                      class="cursor-pointer"
                      @click.stop="toggleMenu(index, $event)"
                      :aria-expanded="
                        openMenuIndex === index ? 'true' : 'false'
                      "
                    />

                    <ul
                      v-if="openMenuIndex === index"
                      class="dropdown-menu show shadow border-0 rounded-3 p-2"
                      :style="dropdownStyle"
                    >
                      <li>
                        <button
                          class="dropdown-item py-2"
                          @click="onEditRole(user)"
                        >
                          Edit Role
                        </button>
                      </li>
                      <li>
                        <button
                          v-if="user.status === 'active'"
                          class="dropdown-item py-2"
                          @click="onSuspendUser(user)"
                        >
                          Suspend
                        </button>
                      </li>
                      <li>
                        <button
                          v-if="user.status === 'suspended'"
                          class="dropdown-item py-2"
                          @click="onReactiveUser(user)"
                        >
                          Reactive
                        </button>
                      </li>
                      <li>
                        <button
                          class="dropdown-item py-2"
                          @click="onViewDetails(user)"
                        >
                          View Details
                        </button>
                      </li>
                    </ul>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Footer Pagination -->
        <div
          class="d-flex justify-content-between align-items-center border-top p-2"
        >
          <div class="d-flex align-items-center gap-2">
            Show
            <select class="form-select d-inline" v-model="pageSize">
              <option>5</option>
              <option>10</option>
              <option>20</option>
            </select>
            <div>of {{ pagination.total }} Items</div>
          </div>
          <nav>
            <ul class="pagination mb-0">
              <li
                :class="[
                  'page-item',
                  { disabled: pagination.current_page === 1 },
                ]"
              >
                <button
                  class="page-link"
                  @click="goToPage(pagination.current_page - 1)"
                >
                  &lt;
                </button>
              </li>

              <li
                v-for="page in pagination.last_page"
                :key="page"
                :class="[
                  'page-item',
                  { active: pagination.current_page === page },
                ]"
              >
                <button class="page-link" @click="goToPage(page)">
                  {{ page }}
                </button>
              </li>

              <li
                :class="[
                  'page-item',
                  {
                    disabled: pagination.current_page === pagination.last_page,
                  },
                ]"
              >
                <button
                  class="page-link"
                  @click="goToPage(pagination.current_page + 1)"
                >
                  &gt;
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
      <!-- Invite User Modal -->
      <InviteUserModalComponent
        :show="showInviteModal"
        :roleList="roleList"
        @close="showInviteModal = false"
        @saved="inviteUser"
      />

      <EditUserModalComponent
        :show="showEditModal"
        :user="selectedUser"
        :roleList="roleList"
        @close="showEditModal = false"
        @updated="onUserUpdated"
      />

      <ReactiveUserModalComponent
        :show="showReactiveModal"
        :user="selectedUser"
        @close="showReactiveModal = false"
        @reactivated="onUserReactivated"
      />

      <SuspendUserModalComponent
        :show="showSuspendModal"
        :user="selectedUser"
        @close="showSuspendModal = false"
        @suspended="onUserSuspended"
      />
    </div>
  </main>
</template>

<script>
import {
  getUserManagementList,
  getUserManagementRoles,
} from "@/api/user-management";
import InviteUserModalComponent from "./InviteUserModalComponent.vue";
import EditUserModalComponent from "./EditUserModalComponent.vue";
import ReactiveUserModalComponent from "./ReactiveUserModalComponent.vue";
import SuspendUserModalComponent from "./SuspendUserModalComponent.vue";

export default {
  name: "UserManagementView",
  components: {
    InviteUserModalComponent,
    EditUserModalComponent,
    ReactiveUserModalComponent,
    SuspendUserModalComponent,
  },
  data() {
    return {
      pageSize: 5,
      openMenuIndex: null,
      users: [],
      loading: false,
      pagination: {
        current_page: 1,
        per_page: 5,
        total: 0,
        last_page: 1,
      },
      search: "",
      searchTimeout: null, // <-- add this
      dropdownStyle: {},
      showInviteModal: false,
      showEditModal: false,
      selectedUser: null,
      showReactiveModal: false,
      showSuspendModal: false,
      roleList: [],
      rolesIsLoading: false,
    };
  },
  computed: {
    filteredUsers() {
      return this.users;
    },
  },
  methods: {
    async getRoles() {
      this.rolesIsLoading = true;

      try {
        const response = await getUserManagementRoles();

        if (response && response.status === "success" && response.data) {
          this.roleList = response.data.data || response.data || [];
        } else {
          this.roleList = [];
        }
        console.log("first", this.roleList);
      } catch (error) {
        this.roleList = [];
        this.$swal("Failed to fetch roles data");
      } finally {
        this.rolesIsLoading = false;
      }
    },
    async fetchUserManagementData(
      page = 1,
      perPage = this.pageSize,
      search = this.search
    ) {
      this.loading = true;
      try {
        const params = {
          page,
          per_page: perPage,
          search: search || "",
        };

        const response = await getUserManagementList(params);

        if (response && response.status === "success" && response.data) {
          this.users = response.data.data || response.data || [];
          // Laravel pagination format
          this.pagination.current_page = response.data.current_page;
          this.pagination.per_page = response.data.per_page;
          this.pagination.total = response.data.total;
          this.pagination.last_page = response.data.last_page;
        } else {
          this.users = [];
        }
      } catch (error) {
        this.users = [];
        this.$swal("Failed to fetch users data");
      } finally {
        this.loading = false;
      }
    },

    async inviteUser() {
      this.showInviteModal = false;
      await this.fetchUserManagementData();
      this.$swal("Success!", "User invitation sent!", "success");
    },

    async onUserUpdated() {
      this.showEditModal = false;
      this.selectedUser = null;
      await this.fetchUserManagementData(
        this.pagination.current_page,
        this.pageSize,
        this.search
      );
      this.$swal("Success!", "User has been updated!", "success");
    },

    async onUserReactivated() {
      this.showReactiveModal = false;
      this.selectedUser = null;
      await this.fetchUserManagementData(
        this.pagination.current_page,
        this.pageSize,
        this.search
      );
      this.$swal("Success!", "User has been reactivated!", "success");
    },

    async onUserSuspended() {
      this.showSuspendModal = false;
      this.selectedUser = null;
      await this.fetchUserManagementData(
        this.pagination.current_page,
        this.pageSize,
        this.search
      );
      this.$swal("Success!", "User has been suspended!", "success");
    },

    onEditRole(user) {
      this.selectedUser = user;
      this.showEditModal = true;
    },

    onReactiveUser(user) {
      this.selectedUser = user;
      this.showReactiveModal = true;
    },

    onSuspendUser(user) {
      this.selectedUser = user;
      this.showSuspendModal = true;
    },

    toggleMenu(i, event) {
      if (this.openMenuIndex === i) {
        this.openMenuIndex = null;
        return;
      }
      this.openMenuIndex = i;

      const rect = event.target.getBoundingClientRect();
      let left = rect.left;

      const screenWidth = window.innerWidth;
      const dropdownWidth = 180;
      const marginRight = 16;

      if (left + dropdownWidth + marginRight > screenWidth) {
        left = screenWidth - dropdownWidth - marginRight;
      }

      this.dropdownStyle = {
        position: "fixed",
        top: rect.bottom + "px",
        left: left + "px",
        zIndex: 1050,
      };
    },

    closeAnyMenu() {
      this.openMenuIndex = null;
    },

    async goToPage(page) {
      if (page < 1 || page > this.pagination.last_page) return;
      await this.fetchUserManagementData(page, this.pageSize, this.search);
    },

    onViewDetails(user) {
      if (!user?.id) {
        this.$swal("Error", "User ID is missing", "error");
        return;
      }
      this.$router.push(`/user-management/${user.id}`);
    },
    capitalizeWords(str) {
      if (!str) return "";
      return str
        .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    formatDate(dateString) {
      const date = new Date(dateString);

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = String(date.getFullYear()).slice(-2);

      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `${day}/${month}/${year}, ${hours}:${minutes}`;
    },
  },
  watch: {
    pageSize(newVal) {
      this.fetchUserManagementData(1, newVal, this.search);
    },
    search(newVal) {
      // Debounce search to avoid spamming API
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.fetchUserManagementData(1, this.pageSize, newVal);
      }, 500); // <-- wait 500ms after user stops typing
    },
  },
  mounted() {
    this.fetchUserManagementData();
    this.getRoles();
    document.addEventListener("click", this.closeAnyMenu);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeAnyMenu);
  },
};
</script>

<style scoped>
.pending-badge {
  display: flex;
  padding: 2px 4px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 100px;
  background: var(--Orange-10, #fff8e5);
}

.text-pending-badge {
  color: var(--Orange-100, #f80);
  text-align: center;

  /* Body / Bold / Xsmall */
  font-family: "IBM Plex Sans Devanagari";
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 14px; /* 116.667% */
  letter-spacing: 0.09px;
}

.active-badge {
  display: flex;
  padding: 2px 4px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 100px;
  background: var(--Green-10, #e3fff1);
}

.text-active-badge {
  color: var(--Green-100, #06c270);
  text-align: center;

  /* Body / Bold / Xsmall */
  font-family: "IBM Plex Sans Devanagari";
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 14px; /* 116.667% */
  letter-spacing: 0.09px;
}

.suspended-badge {
  display: flex;
  padding: 2px 4px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 100px;
  background: var(--Red-10, #ffe5e5);
}

.text-suspended-badge {
  color: var(--Red-100, #ff3b3b);
  text-align: center;

  /* Body / Bold / Xsmall */
  font-family: "IBM Plex Sans Devanagari";
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 14px; /* 116.667% */
  letter-spacing: 0.09px;
}

.custom-button {
  width: 142px;
  height: 40px;
  padding: 8px 16px;
  gap: 10px; /* Ignored unless using flex or grid */
  border-radius: 4px;

  background: linear-gradient(180deg, #43bb75 0%, #1c9762 100%);
  border: 1px solid var(--Green-100, #128050);
  box-shadow: inset 0px 2px 1px 0px #ffffff40;

  color: white;
  font-weight: 600;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.75%;
  vertical-align: middle;
  text-align: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Shared wrapper style (if not fully encapsulated in BaseDateRangePicker) */
.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

/* Base styling for inputs/select (used by search and status) */
.styled-input {
  height: 40px;
  border-radius: 100px !important;
  border: 1px solid #e2e8f0 !important;
  background-color: #f8fafc !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  font-size: 14px !important;
  color: #4a5568;
  box-sizing: border-box;
  width: 100%;
  transition: border-color 0.2s ease-in-out;
}

.styled-input:focus {
  border-color: #4299e1 !important;
  outline: none;
  box-shadow: 0 0 0 1px rgba(66, 153, 225, 0.5);
}

.styled-input::placeholder {
  color: #a0aec0;
  opacity: 1;
}

/* Search Input */
.search-input-wrapper {
  flex-basis: 250px;
}

.search-input.styled-input {
  padding-left: 40px !important;
}

/* Icon positioning (shared) */
.input-icon {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.icon-left {
  left: 12px;
}

@keyframes pulse {
  0% {
    background-color: #e0e0e0;
  }
  50% {
    background-color: #f0f0f0;
  }
  100% {
    background-color: #e0e0e0;
  }
}
</style>
